import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FaSearch, FaSync, FaFilter, FaTimes, FaEye, FaTrash,
  FaCalendarAlt, FaTags, FaChevronDown, FaUser, FaCreditCard,
  FaFileInvoice,  FaSave, FaEdit, FaDownload, 
  FaExclamationTriangle, FaCheckCircle, FaGlobe, FaMoneyBillWave, FaFileExcel
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx"; // Ensure this is installed: npm install xlsx

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ═══════════════════════════════════════════════════
   TOAST COMPONENT
═══════════════════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: { icon: <FaCheckCircle />, label: "Success", accentColor: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", borderColor: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />, label: "Error",   accentColor: "#8b1a1a", iconBg: "rgba(139,26,26,0.1)",  borderColor: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaSearch />, label: "Info",    accentColor: "#8a6020", iconBg: "rgba(201,151,58,0.1)", borderColor: "rgba(201,151,58,0.3)", symbol: "ॐ" }
  };
  const c = config[type] || config.info;

  return (
    <div className="adv-toast-container">
      <div className="adv-toast-card" style={{ "--accent": c.accentColor, "--border-color": c.borderColor }}>
        <div className="adv-toast-top-bar" />
        <span className="adv-toast-corner tl" /><span className="adv-toast-corner tr" />
        <span className="adv-toast-corner bl" /><span className="adv-toast-corner br" />
        <div className="adv-toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="adv-toast-spin-ring" />
          <div className="adv-toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>
        <div className="adv-toast-content">
          <div className="adv-toast-label-row">
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="adv-toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="adv-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accentColor}55,transparent)` }} />
          <p className="adv-toast-message">{message}</p>
        </div>
        <button className="adv-toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>
        <div className="adv-toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   CONFIRM DIALOG
═══════════════════════════════════════════════════ */
function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = "Confirm", danger = false, title = "Confirm Action", icon = null }) {
  const { t } = useTranslation();
  const iconEl = icon || (danger ? <FaExclamationTriangle /> : <FaCheckCircle />);
  const accentColor = danger ? "#8b1a1a" : "#2a6b2a";
  const accentBg = danger ? "rgba(139,26,26,0.12)" : "rgba(42,107,42,0.1)";
  const accentBorder = danger ? "rgba(139,26,26,0.3)" : "rgba(42,107,42,0.28)";

  return createPortal(
    <div className="adv-modal-overlay" onClick={onCancel}>
      <div className="adv-confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="adv-cm-topstrip" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}88, ${accentColor}, ${accentColor}88, transparent)` }} />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-cm-header">
          <div className="adv-cm-icon-wrap" style={{ background: accentBg, borderColor: accentBorder }}>
            <div className="adv-cm-icon-ring" style={{ borderColor: `${accentColor}44` }} />
            <span className="adv-cm-icon" style={{ color: accentColor }}>{iconEl}</span>
          </div>
          <div className="adv-cm-title-area">
            <div className="adv-cm-eyebrow">{t("confirmActionEyebrow")}</div>
            <h3 className="adv-cm-title">{title}</h3>
          </div>
        </div>
        <div className="adv-cm-divider">
          <span className="adv-cm-divider-gem" style={{ background: accentColor }} />
        </div>
        <div className="adv-cm-body">
          <p className="adv-cm-message">{message}</p>
        </div>
        <div className="adv-cm-actions">
          <button className="adv-btn adv-btn-ghost" onClick={onCancel}>{t("btnCancel")}</button>
          <button
            className="adv-btn adv-cm-confirm-btn"
            style={{ "--cbg": danger ? "linear-gradient(135deg,#5a0000,#8b1a1a)" : "linear-gradient(135deg,#1a4a1a,#2a6b2a)", "--cborder": accentBorder }}
            onClick={onConfirm}
          >
            {iconEl} {confirmLabel}
          </button>
        </div>
        <div className="adv-cm-bottomstrip" />
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   BOOKING DETAIL MODAL
═══════════════════════════════════════════════════ */
function BookingDetailModal({ booking, onClose, onDelete }) {
  const { t } = useTranslation();
  if (!booking) return null;
  const isOnline = booking.booked_by === 'online';
  const isCash = booking.payment_mode === 'cash';

  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-detail-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <FaFileInvoice className="adv-rm-hicon" />
            </div>
            <div className="adv-rm-header-eyebrow">{t("detailEyebrow")}</div>
            <h2 className="adv-rm-title">{booking.receipt_number}</h2>
            <p className="adv-rm-name-tag">{booking.category_name}</p>
            <div className="adv-rm-badges">
              <span className={`adv-source-badge ${isOnline ? 'online' : 'offline'}`}>
                {isOnline ? <FaGlobe /> : <FaMoneyBillWave />} {isOnline ? t("sourceBadgeOnline") : t("sourceBadgeOffline")}
              </span>
              <span className="adv-pay-mode-badge">
                {booking.payment_mode?.toUpperCase() || 'PENDING'}
              </span>
            </div>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>
        <div className="adv-detail-scroll-body">
          <div className="adv-detail-section-label">{t("detailSummary")}</div>
          <div className="adv-detail-info-grid">
            <div className="adv-di-field"><div className="adv-di-icon"><FaUser /></div><div><div className="adv-di-label">{t("detailDevoteeMobile")}</div><div className="adv-di-val">{booking.mobile_number}</div></div></div>
            <div className="adv-di-field"><div className="adv-di-icon"><FaCalendarAlt /></div><div><div className="adv-di-label">{t("detailBookingDate")}</div><div className="adv-di-val">{new Date(booking.booking_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div></div></div>
            <div className="adv-di-field"><div className="adv-di-icon"><FaTags /></div><div><div className="adv-di-label">{t("detailCategory")}</div><div className="adv-di-val">{booking.category_name}</div></div></div>
            <div className="adv-di-field"><div className="adv-di-icon"><FaCreditCard /></div><div><div className="adv-di-label">{t("detailTotalPaid")}</div><div className="adv-di-val" style={{fontWeight: "700", color: "#2a6b2a"}}>₹{parseFloat(booking.amount).toLocaleString('en-IN')}</div></div></div>
          </div>
          {booking.persons && booking.persons.length > 0 && (
            <>
              <div className="adv-detail-section-label" style={{marginTop: "18px"}}>{t("detailPersons")} ({booking.total_persons})</div>
              <div className="adv-persons-list">
                {booking.persons.map((p, idx) => (
                  <div key={idx} className="adv-person-chip">
                    <span className="adv-p-name">{p.name}</span>
                    {p.nakshathram && <span className="adv-p-nakshathram">{p.nakshathram}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="adv-detail-section-label" style={{marginTop: "22px"}}>{t("detailAdminActions")}</div>
          <div className="adv-detail-action-grid">
             <button className="adv-action-tile danger" onClick={() => onDelete(booking)}>
              <FaTrash className="adv-at-icon" /><span className="adv-at-label">{t("detailDeleteBooking")}</span><span className="adv-at-desc">{t("detailDeleteDesc")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
export default function ABookings() {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  
  // Filter States
  const [specificDate, setSpecificDate] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  
  // UI States
  const [toast, setToast] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [logoFace, setLogoFace] = useState("om");
  const searchRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  const showToast = (message, type = "info") => setToast({ message, type });

  // --- FETCH DATA ---
  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/vazhipadu-categories`);
      const data = await res.json();
      setCategories(data || []);
    } catch (err) { console.error("Failed to fetch categories"); }
  }, [API]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/bookings`);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setBookings(data);
      setFiltered(data);
    } catch (err) {
      showToast(t("toastFetchFailed"), "error");
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchCategories(); fetchBookings(); }, [fetchCategories, fetchBookings]);

  // --- FILTERING LOGIC (ALL FIXED) ---
  useEffect(() => {
    let result = [...bookings];

    // 1. Search — receipt number or mobile
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(b =>
        b.receipt_number?.toLowerCase().includes(q) ||
        b.mobile_number?.includes(q)
      );
    }

    // 2. Category Filter — compare by category_name (always present in API response)
    if (selectedCategory !== "all") {
      result = result.filter(b =>
        String(b.category_name).toLowerCase() === String(selectedCategory).toLowerCase()
      );
    }

    // 3. Source Filter
    if (selectedSource !== "all") {
      if (selectedSource === "online") result = result.filter(b => b.booked_by === "online");
      else if (selectedSource === "offline") result = result.filter(b => b.booked_by === "admin");
    }

    // Helper: extract "YYYY-MM-DD" handling ALL formats pg driver sends:
    //
    // Case 1: "2026-03-07"              → plain DATE string, no conversion needed
    // Case 2: "2026-03-07T00:00:00.000Z"→ UTC midnight, needs LOCAL getters (IST = +5:30)
    // Case 3: "2026-03-06T18:30:00.000Z"→ pg shifted IST midnight to UTC, needs LOCAL getters
    // Case 4: "2026-03-07 23:28:39"     → plain timestamp string (no Z), slice directly
    //
    // Rule: if the string ends with "Z" (UTC), use local Date getters to recover IST date.
    //       if it has no Z, it is already in local time — just slice first 10 chars.
    const toDateStr = (val) => {
      if (!val) return "";
      const s = String(val).trim();
      // Case 1 & 4: no Z suffix → already local time, safe to slice
      if (!s.endsWith("Z")) return s.slice(0, 10);
      // Case 2 & 3: ends with Z → UTC string, extract LOCAL date parts
      const d = new Date(s);
      if (isNaN(d)) return s.slice(0, 10); // unparseable fallback
      const yyyy = d.getFullYear();
      const mm   = String(d.getMonth() + 1).padStart(2, "0");
      const dd   = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    // 4. Specific Date — pure string comparison, zero timezone issues
    if (specificDate) {
      result = result.filter(b => toDateStr(b.booking_date) === specificDate);
    }

    // 5. Date Range — string comparison (YYYY-MM-DD sorts lexicographically)
    if (!specificDate && (dateRange.start || dateRange.end)) {
      result = result.filter(b => {
        const bDate = toDateStr(b.booking_date);
        if (dateRange.start && dateRange.end)
          return bDate >= dateRange.start && bDate <= dateRange.end;
        if (dateRange.start) return bDate >= dateRange.start;
        if (dateRange.end)   return bDate <= dateRange.end;
        return true;
      });
    }

    // 6. Month Filter — string-based, no Date object needed
    if (!specificDate && !dateRange.start && !dateRange.end && selectedMonth) {
      result = result.filter(b => toDateStr(b.booking_date).startsWith(selectedMonth));
    }

    setFiltered(result);
  }, [search, selectedCategory, selectedSource, specificDate, dateRange, selectedMonth, bookings]);

  // --- HANDLERS ---

  // Handle Specific Date — independent state, clears month only
  const handleSpecificDateChange = (val) => {
    setSpecificDate(val);
    setSelectedMonth("");
    // If user picks a specific date, clear range to avoid confusion
    if (val) setDateRange({ start: "", end: "" });
  };

  // Handle Range — clears specificDate and month
  const handleRangeChange = (field, val) => {
    setDateRange(prev => ({ ...prev, [field]: val }));
    setSpecificDate("");
    setSelectedMonth("");
  };

  const handleDelete = (b) => {
    setConfirmDialog({
      title: t("confirmDeleteTitle"),
      message: `Are you sure you want to delete booking ${b.receipt_number}? This action cannot be undone.`,
      confirmLabel: t("confirmDeleteBtn"),
      danger: true,
      icon: <FaTrash />,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const res = await fetch(`${API}/api/admin/bookings/${b.booking_id}`, { method: "DELETE" });
          if (!res.ok) throw new Error("Failed to delete");
          showToast(t("toastDeleteSuccess"), "success");
          fetchBookings();
          setSelectedBooking(null);
        } catch (err) {
          showToast(err.message, "error");
        } finally {
          setActionLoading(false);
          setConfirmDialog(null);
        }
      }
    });
  };

  // --- EXCEL EXPORT (Fixed: null safety + persons JSON parse + column widths) ---
  const handleExport = () => {
    if (filtered.length === 0) {
      showToast(t("toastExportEmpty"), "info");
      return;
    }

    const exportData = [];

    filtered.forEach(b => {
      // Safe-parse persons: pg driver may return JSON string instead of array
      let persons = b.persons;
      if (typeof persons === "string") {
        try { persons = JSON.parse(persons); } catch { persons = []; }
      }
      if (!Array.isArray(persons)) persons = [];

      // Safe amount: can be null when payment record is missing
      const amount = b.amount != null && !isNaN(parseFloat(b.amount))
        ? parseFloat(b.amount)
        : parseFloat(b.unit_price || 0);

      // Format booking date nicely
      const bookingDate = b.booking_date
        ? new Date(b.booking_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        : "";

      const baseRow = {
        [t("exportColReceipt")]: b.receipt_number || "",
        [t("exportColDate")]:   bookingDate,
        [t("exportColCategory")]: b.category_name || "",
        [t("exportColMobile")]: b.mobile_number || "",
        [t("exportColPersons")]:  b.total_persons || 1,
        [t("exportColAmount")]: amount,
        [t("exportColPayMode")]:   b.payment_mode || "cash",
        [t("exportColSource")]: b.booked_by === "online" ? t("exportSourceOnline") : t("exportSourceCounter"),
        [t("exportColStatus")]: b.payment_status || "",
      };

      if (persons.length > 0) {
        persons.forEach((p, idx) => {
          exportData.push({
            ...baseRow,
            [t("exportColPersonNo")]:    idx + 1,
            [t("exportColPersonName")]: p.name || "",
            [t("exportColNakshathram")]: p.nakshathram || "N/A",
          });
        });
      } else {
        exportData.push({
          ...baseRow,
          [t("exportColPersonNo")]:    1,
          [t("exportColPersonName")]: "N/A",
          [t("exportColNakshathram")]: "N/A",
        });
      }
    });

    // Build sheet with auto column widths
    const ws = XLSX.utils.json_to_sheet(exportData);
    const colKeys = Object.keys(exportData[0] || {});
    ws["!cols"] = colKeys.map(key => ({
      wch: Math.max(key.length + 2, ...exportData.map(r => String(r[key] ?? "").length + 2), 10)
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("exportSheetName"));
    const filename = `Temple_Bookings_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, filename);
    showToast(t("toastExportSuccess").replace("{count}", exportData.length), "success");
  };

  const clearFilters = () => {
    setSearch("");
    setSpecificDate("");
    setDateRange({ start: "", end: "" });
    setSelectedMonth("");
    setSelectedCategory("all");
    setSelectedSource("all");
  };

  const activeFilterCount =
    (search ? 1 : 0) +
    (selectedCategory !== "all" ? 1 : 0) +
    (selectedSource !== "all" ? 1 : 0) +
    (specificDate ? 1 : 0) +
    (dateRange.start || dateRange.end ? 1 : 0) +
    (selectedMonth ? 1 : 0);

  return (
    <>
      <AbookingsStyles />

      {/* ── HERO ── */}
      <div className="adv-hero">
        <div className="adv-hero-bg" />
        <div className="adv-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="adv-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>
        <div className="adv-hero-medallion">
          <div className="adv-hero-med-ring" />
          <div className="adv-hero-med-ring adv-hero-med-ring-2" />
          <div className={`adv-hero-face ${logoFace === "om" ? "visible" : ""}`}>
            <span className="adv-hero-om">ॐ</span>
          </div>
          <div className={`adv-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple" className="adv-hero-logo-img" />
          </div>
          <div className="adv-hero-med-halo" />
        </div>
        <div className="adv-hero-text">
          <div className="adv-hero-eyebrow">✦ {t("pageEyebrow")} ✦</div>
          <h1 className="adv-hero-title">{t("pageTitle")}</h1>
          <p className="adv-hero-sub">{t("pageSub")}</p>
        </div>
        <div className="adv-hero-rule" />
        <div className="adv-stat-row">
          <div className="adv-stat-card" style={{ "--scolor": "#c9973a" }}>
            <div className="adv-stat-val">{filtered.length}</div>
            <div className="adv-stat-label">{t("statShowing")}</div>
          </div>
          <div className="adv-stat-card" style={{ "--scolor": "#2a6b2a" }}>
            <div className="adv-stat-val">{bookings.length}</div>
            <div className="adv-stat-label">{t("statTotal")}</div>
          </div>
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="adv-toolbar">
        <div className="adv-search-wrap">
          <FaSearch className="adv-search-icon" />
          <input
            ref={searchRef}
            className="adv-search-input"
            placeholder={t("searchPlaceholder")}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="adv-search-clear" onClick={() => setSearch("")}><FaTimes /></button>}
        </div>

        <button className="adv-filter-trigger-btn" onClick={() => setShowFilterPanel(!showFilterPanel)}>
          <FaFilter /> {t("btnFilters")}
          {activeFilterCount > 0 && <span className="adv-filter-badge">{activeFilterCount}</span>}
          <FaChevronDown className={`adv-dd-chevron ${showFilterPanel ? "open" : ""}`} />
        </button>

        <div className="adv-toolbar-btns">
          <button className="adv-action-btn adv-btn-export" onClick={handleExport}>
            <FaFileExcel /> {t("btnExport")}
          </button>
          {(activeFilterCount > 0) && (
            <button className="adv-action-btn adv-btn-clear" onClick={clearFilters}>
              <FaTimes /> {t("btnClear")}
            </button>
          )}
          <button className="adv-action-btn adv-btn-refresh" onClick={fetchBookings}>
            <FaSync className={loading ? "spin" : ""} /> {t("btnRefresh")}
          </button>
        </div>
      </div>

      {/* ── FILTER PANEL ── */}
      {showFilterPanel && (
        <div className="adv-filter-panel">
          <div className="adv-filter-row">
            {/* Source Filter */}
            <div className="adv-filter-item">
              <label className="adv-filter-label"><FaGlobe /> {t("filterSource")}</label>
              <select 
                className="adv-filter-select" 
                value={selectedSource} 
                onChange={(e) => setSelectedSource(e.target.value)}
              >
                <option value="all">{t("filterSourceAll")}</option>
                <option value="online">{t("filterSourceOnline")}</option>
                <option value="offline">{t("filterSourceOffline")}</option>
              </select>
            </div>

            {/* Category Filter */}
            <div className="adv-filter-item">
              <label className="adv-filter-label"><FaTags /> {t("filterCategory")}</label>
              <select 
                className="adv-filter-select" 
                value={selectedCategory} 
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">{t("filterCategoryAll")}</option>
                {categories.map(c => (
                  <option key={c.vazhipadu_id} value={c.name}>{c.name} (₹{c.amount})</option>
                ))}
              </select>
            </div>

            {/* Specific Date — its own independent state */}
            <div className="adv-filter-item">
              <label className="adv-filter-label"><FaCalendarAlt /> {t("filterSpecificDate")}</label>
              <input
                type="date"
                className="adv-filter-input"
                value={specificDate}
                onChange={(e) => handleSpecificDateChange(e.target.value)}
              />
              {specificDate && (
                <button className="adv-filter-clear-btn" onClick={() => setSpecificDate("")}>{t("filterClearDate")}</button>
              )}
            </div>

            {/* Date Range — only active when specificDate is empty */}
            <div className={`adv-filter-item adv-filter-range ${specificDate ? "adv-filter-disabled" : ""}`}>
              <label className="adv-filter-label"><FaCalendarAlt /> {t("filterDateRange")} {specificDate && <span className="adv-filter-note">{t("filterClearDateFirst")}</span>}</label>
              <div className="adv-range-inputs">
                <input
                  type="date"
                  className="adv-filter-input"
                  value={dateRange.start}
                  onChange={(e) => handleRangeChange("start", e.target.value)}
                  disabled={!!specificDate}
                />
                <span className="adv-range-sep">→</span>
                <input
                  type="date"
                  className="adv-filter-input"
                  value={dateRange.end}
                  onChange={(e) => handleRangeChange("end", e.target.value)}
                  disabled={!!specificDate}
                />
              </div>
              {(dateRange.start || dateRange.end) && !specificDate && (
                <button className="adv-filter-clear-btn" onClick={() => setDateRange({ start: "", end: "" })}>{t("filterClearRange")}</button>
              )}
            </div>

            {/* Month Selector — only active when no date filters set */}
            <div className={`adv-filter-item ${(specificDate || dateRange.start || dateRange.end) ? "adv-filter-disabled" : ""}`}>
              <label className="adv-filter-label">
                <FaCalendarAlt /> {t("filterMonth")}
                {(specificDate || dateRange.start || dateRange.end) && <span className="adv-filter-note">{t("filterClearDateFiltersFirst")}</span>}
              </label>
              <input
                type="month"
                className="adv-filter-input"
                value={selectedMonth}
                onChange={(e) => { setSelectedMonth(e.target.value); setSpecificDate(""); setDateRange({ start: "", end: "" }); }}
                disabled={!!(specificDate || dateRange.start || dateRange.end)}
              />
              {selectedMonth && !(specificDate || dateRange.start || dateRange.end) && (
                <button className="adv-filter-clear-btn" onClick={() => setSelectedMonth("")}>{t("filterClearMonth")}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── RESULT INFO ── */}
      <div className="adv-result-info">
        {!loading && <span>{t("displaying")} <strong>{filtered.length}</strong> {t("bookings")}</span>}
      </div>

      {/* ── TABLE ── */}
      <div className="adv-table-wrap">
        {loading ? (
          <div className="adv-loader-wrap"><div className="adv-loader-om">ॐ</div><p>{t("loading")}</p></div>
        ) : filtered.length === 0 ? (
          <div className="adv-empty">
            <div className="adv-empty-om">📜</div>
            <h3 className="adv-empty-title">{t("emptyTitle")}</h3>
            <p className="adv-empty-sub">{t("emptySub")}</p>
            <button className="adv-btn adv-btn-ghost" onClick={clearFilters}>{t("btnClearFilters")}</button>
          </div>
        ) : (
          <div className="adv-table-scroll-container">
            <table className="adv-table adv-desktop-only">
              <thead>
                <tr>
                  <th>{t("tableReceiptNo")}</th>
                  <th>{t("tableDate")}</th>
                  <th>{t("detailCategory")}</th>
                  <th>{t("tableDevotee")}</th>
                  <th>{t("tableSource")}</th>
                  <th>{t("tableAmount")}</th>
                  <th>{t("tableStatus")}</th>
                  <th>{t("tableActions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b, i) => (
                  <tr key={b.booking_id} className="adv-table-row" style={{ animationDelay: `${i * 0.03}s` }}>
                    <td className="adv-td-receipt"><span className="adv-receipt-badge">{b.receipt_number}</span></td>
                    <td className="adv-td-date">{new Date(b.booking_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td>
                      <div className="adv-category-cell">
                        <span className="adv-cat-name">{b.category_name}</span>
                        <span className="adv-cat-price">₹{b.unit_price}</span>
                      </div>
                    </td>
                    <td className="adv-td-mobile">{b.mobile_number}</td>
                    <td className="adv-td-source">
                      {b.booked_by === 'online' ? (
                        <span className="adv-source-badge online"><FaGlobe /> {t("sourceOnline")}</span>
                      ) : (
                        <span className="adv-source-badge offline"><FaMoneyBillWave /> {t("sourceCounter")}</span>
                      )}
                    </td>
                    <td>
                      <div className="adv-payment-cell">
                        <span className="adv-pay-amt">₹{parseFloat(b.amount).toLocaleString('en-IN')}</span>
                        <span className="adv-pay-mode">{b.payment_mode}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`adv-status-pill ${b.payment_status === 'success' ? 'success' : 'pending'}`}>
                        {b.payment_status}
                      </span>
                    </td>
                    <td>
                      <div className="adv-action-cell">
                        <button className="adv-icon-btn adv-btn-view" title="View Details" onClick={() => setSelectedBooking(b)}><FaEye /></button>
                        <button className="adv-icon-btn adv-btn-delete-sm" title="Delete" onClick={() => handleDelete(b)}><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="adv-mobile-only adv-card-list">
              {filtered.map((b, i) => (
                <div key={b.booking_id} className="adv-mob-card">
                  <div className="adv-mob-card-header">
                    <div className="adv-mob-receipt">{b.receipt_number}</div>
                    <span className={`adv-status-pill ${b.payment_status === 'success' ? 'success' : 'pending'}`}>{b.payment_status}</span>
                  </div>
                  <div className="adv-mob-meta">
                    <div className="adv-mob-meta-item"><FaCalendarAlt className="adv-mob-meta-icon" /><span>{new Date(b.booking_date).toLocaleDateString("en-IN")}</span></div>
                    <div className="adv-mob-meta-item"><FaTags className="adv-mob-meta-icon" /><span>{b.category_name}</span></div>
                    <div className="adv-mob-meta-item"><FaUser className="adv-mob-meta-icon" /><span>{b.mobile_number} ({b.total_persons} pers.)</span></div>
                    <div className="adv-mob-meta-item"><FaCreditCard className="adv-mob-meta-icon" /><span>₹{parseFloat(b.amount).toLocaleString('en-IN')} ({b.payment_mode})</span></div>
                    <div className="adv-mob-meta-item">
                      {b.booked_by === 'online' 
                        ? <span className="adv-source-badge online"><FaGlobe /> {t("sourceOnline")}</span> 
                        : <span className="adv-source-badge offline"><FaMoneyBillWave /> {t("sourceCounter")}</span>
                      }
                    </div>
                  </div>
                  <div className="adv-mob-actions">
                    <button className="adv-mob-btn mob-view" onClick={() => setSelectedBooking(b)}><FaEye /> {t("btnView")}</button>
                    <button className="adv-mob-btn mob-delete" onClick={() => handleDelete(b)}><FaTrash /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── MODALS ── */}
      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onDelete={handleDelete}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          danger={confirmDialog.danger}
          icon={confirmDialog.icon}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════ */
function AbookingsStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-rich: #3d0c0c;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --parchment-dark: #f5e8cc;
        --text-dark: #1a0404; --text-mid: #4a1a08;
      }

      /* ══ HERO ══ */
      .adv-hero { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); padding: 40px 32px 0; display: flex; flex-direction: column; align-items: center; border-radius: 16px 16px 0 0; }
      .adv-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .adv-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .adv-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: heroRingPulse 4s ease-in-out infinite alternate; }
      .adv-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes heroRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }
      .adv-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,248,231,0.06); }
      .adv-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: slowSpin 10s linear infinite; }
      .adv-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .adv-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .adv-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .adv-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .adv-hero-om { font-size: 34px; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); color: var(--gold-bright); }
      .adv-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }
      .adv-hero-text { text-align: center; position: relative; z-index: 1; }
      .adv-hero-eyebrow { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .adv-hero-title { font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .adv-hero-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 20px; }
      .adv-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto 24px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-stat-row { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; padding: 0 0 28px; position: relative; z-index: 1; }
      .adv-stat-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(201,151,58,0.18); padding: 14px 22px; text-align: center; cursor: pointer; clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%); transition: background 0.2s, border-color 0.2s; min-width: 90px; }
      .adv-stat-val { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; color: var(--scolor, var(--gold-bright)); }
      .adv-stat-label { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(245,228,176,0.4); margin-top: 3px; }

      /* ══ TOOLBAR ══ */
      .adv-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; padding: 20px 28px 12px; background: var(--parchment); border-bottom: 1px solid rgba(201,151,58,0.15); box-shadow: 0 4px 12px rgba(26,4,4,0.06); }
      .adv-search-wrap { flex: 1; min-width: 220px; display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s; }
      .adv-search-wrap:focus-within { border-color: var(--gold-mid); box-shadow: 0 0 0 3px rgba(201,151,58,0.1); }
      .adv-search-icon { margin: 0 10px 0 18px; color: var(--gold-dim); font-size: 13px; flex-shrink: 0; }
      .adv-search-input { flex: 1; border: none; outline: none; background: transparent; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 11px 8px; }
      .adv-search-input::placeholder { color: rgba(74,26,8,0.28); font-style: italic; }
      .adv-search-clear { background: none; border: none; cursor: pointer; padding: 0 14px; color: rgba(74,26,8,0.35); font-size: 11px; transition: color 0.2s; }
      .adv-search-clear:hover { color: #8b1a1a; }

      .adv-filter-trigger-btn { display: flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); border: 1px solid rgba(201,151,58,0.4); color: var(--gold-bright); padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.2s; position: relative; }
      .adv-filter-trigger-btn:hover { border-color: var(--gold-mid); background: linear-gradient(135deg, #3d1010, #2a0808); }
      .adv-filter-badge { position: absolute; top: -6px; right: -6px; background: #8b1a1a; color: white; font-size: 9px; width: 16px; height: 16px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: sans-serif; font-weight: bold; border: 1px solid var(--parchment); }
      .adv-dd-chevron { font-size: 9px; color: rgba(232,196,106,0.5); transition: transform 0.25s; }
      .adv-dd-chevron.open { transform: rotate(180deg); }

      .adv-toolbar-btns { display: flex; gap: 8px; margin-left: auto; }
      .adv-action-btn { display: flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.25s; }
      .adv-btn-export { background: linear-gradient(135deg, #1a4d1a, #2a6b2a); color: #c8f0c8; border: 1px solid rgba(42,107,42,0.4); }
      .adv-btn-export:hover { filter: brightness(1.1); }
      .adv-btn-refresh { background: rgba(201,151,58,0.1); border: 1px solid rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-refresh:hover { background: rgba(201,151,58,0.18); }
      .adv-btn-clear { background: rgba(139,26,26,0.08); border: 1px solid rgba(139,26,26,0.3); color: #8b1a1a; }
      .adv-btn-clear:hover { background: rgba(139,26,26,0.15); }
      .spin { animation: spinIcon 0.8s linear infinite; }
      @keyframes spinIcon { to { transform: rotate(360deg); } }

      /* ══ FILTER PANEL ══ */
      .adv-filter-panel { background: rgba(232,196,106,0.05); border-bottom: 1px solid rgba(201,151,58,0.15); padding: 16px 28px; animation: slideDown 0.3s ease; }
      @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      .adv-filter-row { display: flex; flex-wrap: wrap; gap: 20px; align-items: flex-end; }
      .adv-filter-item { display: flex; flex-direction: column; gap: 6px; min-width: 160px; flex: 1; }
      .adv-filter-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-dim); display: flex; align-items: center; gap: 5px; }
      .adv-filter-input, .adv-filter-select { width: 100%; padding: 8px 10px; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); font-family: 'EB Garamond', serif; font-size: 14px; color: var(--text-dark); outline: none; transition: border-color 0.2s; clip-path: polygon(6px 0%, calc(100% - 6px) 0%, 100% 50%, calc(100% - 6px) 100%, 6px 100%, 0% 50%); }
      .adv-filter-input:focus, .adv-filter-select:focus { border-color: var(--gold-mid); }
      .adv-filter-range { min-width: 300px; }
      .adv-range-inputs { display: flex; align-items: center; gap: 8px; width: 100%; }
      .adv-range-sep { color: var(--gold-dim); }
      .adv-filter-clear-btn { margin-top: 4px; display: inline-block; background: none; border: none; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #8b1a1a; cursor: pointer; padding: 2px 0; opacity: 0.7; transition: opacity 0.2s; }
      .adv-filter-clear-btn:hover { opacity: 1; }
      .adv-filter-note { font-family: 'EB Garamond', serif; font-style: italic; font-size: 10px; letter-spacing: 0; text-transform: none; color: rgba(139,26,26,0.6); margin-left: 5px; font-weight: 400; }
      .adv-filter-disabled { opacity: 0.4; pointer-events: none; }
      .adv-filter-disabled .adv-filter-input { background: #f0ece4 !important; cursor: not-allowed; }

      /* ══ TABLE SCROLL CONTAINER ══ */
      .adv-table-scroll-container { overflow-x: auto; max-width: 100%; }
      .adv-table-wrap { padding: 0 28px 40px; background: var(--parchment); }
      .adv-table { width: 100%; border-collapse: separate; border-spacing: 0 6px; font-family: 'EB Garamond', serif; min-width: 1000px; } 
      .adv-table thead tr { background: linear-gradient(135deg, var(--hero-1) 0%, var(--hero-2) 55%, var(--hero-3) 100%); }
      .adv-table thead tr th { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-bright); padding: 14px 14px; text-align: left; border-bottom: 1px solid rgba(201,151,58,0.25); white-space: nowrap; position: relative; }
      .adv-table thead tr th:first-child { border-radius: 3px 0 0 3px; padding-left: 18px; }
      .adv-table thead tr th:last-child { border-radius: 0 3px 3px 0; }
      .adv-table thead tr th::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-table-row { background: linear-gradient(135deg, #FDF3E3, #faf0dc); border: 1px solid rgba(201,151,58,0.15); animation: rowIn 0.35s ease both; transition: box-shadow 0.2s, transform 0.2s; }
      .adv-table-row:hover { box-shadow: 0 4px 18px rgba(26,4,4,0.1); transform: translateY(-1px); }
      @keyframes rowIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      .adv-table-row td { padding: 12px 14px; vertical-align: middle; font-size: 14px; color: var(--text-mid); border-top: 1px solid rgba(201,151,58,0.1); border-bottom: 1px solid rgba(201,151,58,0.1); }
      .adv-table-row td:first-child { border-left: 1px solid rgba(201,151,58,0.1); border-radius: 2px 0 0 2px; }
      .adv-table-row td:last-child  { border-right: 1px solid rgba(201,151,58,0.1); border-radius: 0 2px 2px 0; }
      
      .adv-td-receipt { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 0.5px; color: var(--gold-dim); width: 130px; }
      .adv-receipt-badge { background: rgba(201,151,58,0.1); padding: 3px 8px; border-radius: 2px; border: 1px solid rgba(201,151,58,0.2); display: inline-block; }
      .adv-td-date { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(74,26,8,0.6); width: 100px; }
      
      .adv-category-cell { display: flex; flex-direction: column; gap: 2px; }
      .adv-cat-name { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; color: var(--text-dark); }
      .adv-cat-price { font-family: 'EB Garamond', serif; font-style: italic; font-size: 12px; color: rgba(74,26,8,0.5); }
      
      .adv-td-mobile { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.5px; width: 120px; }
      .adv-td-source { width: 100px; }
      .adv-source-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 9px; font-weight: 700; text-transform: uppercase; padding: 3px 8px; border-radius: 12px; letter-spacing: 0.5px; }
      .adv-source-badge.online { background: rgba(42, 107, 42, 0.1); color: #2a6b2a; border: 1px solid rgba(42, 107, 42, 0.2); }
      .adv-source-badge.offline { background: rgba(201, 151, 58, 0.1); color: var(--gold-mid); border: 1px solid rgba(201, 151, 58, 0.2); }

      .adv-td-persons { width: 80px; }
      .adv-payment-cell { display: flex; flex-direction: column; gap: 2px; }
      .adv-pay-amt { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; color: var(--text-dark); }
      .adv-pay-mode { font-family: 'EB Garamond', serif; font-style: italic; font-size: 11px; color: rgba(74,26,8,0.5); text-transform: capitalize; }
      
      .adv-status-pill { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 4px 10px; border-radius: 1px; white-space: nowrap; }
      .adv-status-pill.success { color: #2a6b2a; background: rgba(42,107,42,0.1); border: 1px solid rgba(42,107,42,0.25); }
      .adv-status-pill.pending { color: #c9973a; background: rgba(201,151,58,0.1); border: 1px solid rgba(201,151,58,0.25); }
      
      .adv-action-cell { display: flex; gap: 5px; align-items: center; }
      .adv-icon-btn { width: 30px; height: 30px; border: 1px solid; border-radius: 2px; display: flex; align-items: center; justify-content: center; font-size: 12px; cursor: pointer; background: none; transition: all 0.18s; flex-shrink: 0; }
      .adv-btn-view { border-color: rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-view:hover { background: rgba(201,151,58,0.12); color: var(--gold-mid); }
      .adv-btn-delete-sm { border-color: rgba(139,26,26,0.25); color: rgba(139,26,26,0.7); }
      .adv-btn-delete-sm:hover { background: rgba(139,26,26,0.08); color: #8b1a1a; }

      /* ══ MODAL STYLES ══ */
      .adv-rm-badges { display: flex; gap: 8px; margin-top: 8px; justify-content: center; }
      .adv-pay-mode-badge { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-mid); background: rgba(74,26,8,0.05); padding: 2px 8px; border-radius: 2px; border: 1px solid rgba(74,26,8,0.1); }

      .adv-persons-list { display: flex; flex-wrap: wrap; gap: 8px; }
      .adv-person-chip { display: inline-flex; align-items: center; gap: 6px; background: #fff9f0; border: 1px solid rgba(201,151,58,0.2); padding: 6px 12px; border-radius: 20px; }
      .adv-p-name { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; color: var(--text-mid); }
      .adv-p-nakshathram { font-family: 'EB Garamond', serif; font-style: italic; font-size: 10px; color: rgba(74,26,8,0.4); padding-left: 6px; border-left: 1px solid rgba(201,151,58,0.2); }

      /* ══ REUSABLE COMPONENTS ══ */
      .adv-loader-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 0; gap: 14px; }
      .adv-loader-om { font-family: 'Cinzel Decorative', cursive; font-size: 48px; color: var(--gold-bright); animation: omGlow 2s ease-in-out infinite alternate; filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); }
      @keyframes omGlow { from { opacity: 0.6; } to { opacity: 1; filter: drop-shadow(0 0 20px rgba(232,196,106,0.6)); } }
      
      .adv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 70px 0; gap: 14px; text-align: center; }
      .adv-empty-om { font-size: 52px; opacity: 0.25; }
      .adv-empty-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; color: var(--crimson-rich); }
      .adv-empty-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.45); max-width: 320px; }

      .adv-modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(8,1,1,0.85); backdrop-filter: blur(7px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: overlayIn 0.2s ease; }
      @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
      
      .adv-regal-modal { position: relative; width: 100%; background: linear-gradient(160deg, #FDF3E3, #faf0dc 55%, #f5e8cc); border: 1px solid rgba(201,151,58,0.35); box-shadow: 0 36px 110px rgba(26,4,4,0.7), 0 0 0 1px rgba(201,151,58,0.06) inset; animation: regalIn 0.35s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      @keyframes regalIn { from { opacity: 0; transform: scale(0.93) translateY(26px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      .adv-detail-regal { max-width: 540px; max-height: 90vh; overflow-y: auto; }
      .adv-detail-regal::-webkit-scrollbar { width: 6px; }
      .adv-detail-regal::-webkit-scrollbar-track { background: linear-gradient(180deg, #f5e8cc, #faf0dc); border-radius: 10px; }
      .adv-detail-regal::-webkit-scrollbar-thumb { background: linear-gradient(180deg, var(--hero-1), #5a1515, var(--gold-mid)); border-radius: 10px; border: 1px solid rgba(232,196,106,0.3); }

      .adv-rm-topglow { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent 0%, rgba(201,151,58,0.2) 20%, rgba(232,196,106,0.9) 50%, rgba(201,151,58,0.2) 80%, transparent 100%); z-index: 10; }
      .adv-cm-corner { position: absolute; width: 16px; height: 16px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 10; }
      .adv-cm-corner.tl { top: 14px; left: 14px; border-width: 1.5px 0 0 1.5px; }
      .adv-cm-corner.tr { top: 14px; right: 14px; border-width: 1.5px 1.5px 0 0; }
      .adv-cm-corner.bl { bottom: 14px; left: 14px; border-width: 0 0 1.5px 1.5px; }
      .adv-cm-corner.br { bottom: 14px; right: 14px; border-width: 0 1.5px 1.5px 0; }
      .adv-rm-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1), var(--hero-2) 55%, var(--hero-3)); padding: 38px 28px 28px; text-align: center; border-bottom: 1px solid rgba(201,151,58,0.2); }
      .adv-rm-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(150,30,30,0.28) 0%, transparent 65%); pointer-events: none; }
      .adv-rm-header-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
      .adv-rm-close { position: absolute; top: 16px; right: 16px; z-index: 5; background: rgba(255,255,255,0.06); border: 1px solid rgba(232,196,106,0.2); color: rgba(232,196,106,0.55); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: all 0.2s; }
      .adv-rm-close:hover { background: rgba(255,255,255,0.12); color: var(--gold-bright); transform: rotate(90deg); }
      .adv-rm-header-icon-wrap { position: relative; width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); }
      .adv-rm-hicon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.28); animation: slowSpin 9s linear infinite; }
      .adv-rm-hicon { font-size: 28px; color: var(--gold-bright); filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); position: relative; z-index: 1; }
      .adv-rm-header-eyebrow { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 3px; color: rgba(232,196,106,0.3); text-transform: uppercase; }
      .adv-rm-title { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.25; }
      .adv-rm-name-tag { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1px; color: rgba(245,228,176,0.4); }
      .adv-rm-rule { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0 40px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); position: relative; z-index: 1; }
      .adv-rm-rule-ornament { position: absolute; font-size: 12px; color: var(--gold-mid); background: var(--parchment); padding: 0 8px; line-height: 1; }

      .adv-detail-scroll-body { padding: 22px 28px 28px; position: relative; z-index: 1; }
      .adv-detail-section-label { font-family: 'Cinzel', serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 14px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .adv-detail-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px; }
      .adv-di-field { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; background: rgba(201,151,58,0.04); border: 1px solid rgba(201,151,58,0.1); border-radius: 1px; transition: background 0.2s; }
      .adv-di-field:hover { background: rgba(201,151,58,0.08); }
      .adv-di-icon { color: var(--gold-dim); font-size: 13px; margin-top: 3px; flex-shrink: 0; }
      .adv-di-label { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(74,26,8,0.38); margin-bottom: 3px; }
      .adv-di-val { font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-mid); }
      
      .adv-detail-action-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(118px, 1fr)); gap: 8px; }
      .adv-action-tile { display: flex; flex-direction: column; align-items: center; gap: 5px; padding: 14px 10px; border: 1px solid; cursor: pointer; background: none; transition: all 0.2s; position: relative; overflow: hidden; clip-path: polygon(6px 0%,calc(100% - 6px) 0%,100% 6px,100% calc(100% - 6px),calc(100% - 6px) 100%,6px 100%,0 calc(100% - 6px),0 6px); }
      .adv-action-tile::before { content: ''; position: absolute; inset: 0; opacity: 0; transition: opacity 0.2s; }
      .adv-action-tile:hover::before { opacity: 1; }
      .adv-action-tile:hover { transform: translateY(-2px); }
      .adv-at-icon { font-size: 18px; position: relative; z-index: 1; }
      .adv-at-label { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; position: relative; z-index: 1; }
      .adv-at-desc { font-family: 'EB Garamond', serif; font-style: italic; font-size: 10.5px; position: relative; z-index: 1; opacity: 0.7; }
      .adv-action-tile.danger { border-color: rgba(139,26,26,0.22); color: rgba(139,26,26,0.8); }
      .adv-action-tile.danger::before { background: rgba(139,26,26,0.05); }
      .adv-action-tile.danger:hover { border-color: rgba(139,26,26,0.45); box-shadow: 0 4px 18px rgba(139,26,26,0.12); color: #8b1a1a; }
      .adv-action-tile:disabled { opacity: 0.35; cursor: not-allowed; transform: none !important; }

      .adv-confirm-modal { position: relative; width: 100%; max-width: 420px; background: linear-gradient(160deg, #FDF3E3, #faf0dc 55%, #f5e8cc); border: 1px solid rgba(201,151,58,0.35); clip-path: polygon(0 18px,18px 0,calc(100% - 18px) 0,100% 18px,100% calc(100% - 18px),calc(100% - 18px) 100%,18px 100%,0 calc(100% - 18px)); box-shadow: 0 32px 90px rgba(26,4,4,0.68); animation: regalIn 0.3s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      .adv-cm-topstrip { height: 3px; width: 100%; }
      .adv-cm-header { padding: 30px 28px 0; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 10px; position: relative; z-index: 1; }
      .adv-cm-icon-wrap { position: relative; width: 68px; height: 68px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 1px solid; }
      .adv-cm-icon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed; animation: slowSpin 8s linear infinite; }
      .adv-cm-icon { font-size: 24px; position: relative; z-index: 1; }
      .adv-cm-title-area { display: flex; flex-direction: column; align-items: center; gap: 4px; }
      .adv-cm-eyebrow { font-family: 'Cinzel', serif; font-size: 7px; letter-spacing: 3px; color: rgba(74,26,8,0.3); text-transform: uppercase; }
      .adv-cm-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; font-weight: 900; background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-cm-divider { display: flex; align-items: center; justify-content: center; height: 1px; margin: 18px 36px 0; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent); position: relative; }
      .adv-cm-divider-gem { width: 6px; height: 6px; transform: rotate(45deg); position: absolute; }
      .adv-cm-body { padding: 16px 32px 20px; position: relative; z-index: 1; }
      .adv-cm-message { font-family: 'EB Garamond', serif; font-size: 16px; color: var(--text-mid); line-height: 1.7; text-align: center; }
      .adv-cm-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; padding: 0 28px 24px; position: relative; z-index: 1; }
      .adv-cm-confirm-btn { background: var(--cbg, linear-gradient(135deg,#1a4a1a,#2a6b2a)) !important; color: #e8ffe8 !important; border-color: var(--cborder, rgba(42,107,42,0.4)) !important; }
      .adv-cm-confirm-btn:hover { filter: brightness(1.12); }
      .adv-cm-bottomstrip { height: 1px; margin: 0 24px 14px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.25), transparent); }
      .adv-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); transition: all 0.2s; border: 1px solid transparent; }
      .adv-btn:disabled { opacity: 0.38; cursor: not-allowed; }
      .adv-btn-ghost { background: none; font-weight:bold; border-color: rgba(149, 27, 27, 0.5); color: rgb(74, 26, 8); }
      .adv-btn-ghost:hover { border-color: rgba(149, 27, 27, 0.5); background: rgba(201,151,58,0.06); }

      .adv-toast-container { position: fixed; top: 28px; right: 28px; z-index: 2000; animation: toastIn 0.4s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes toastIn { from { opacity: 0; transform: translateX(60px) scale(0.95); } to { opacity: 1; transform: translateX(0) scale(1); } }
      .adv-toast-card { position: relative; width: 340px; background: linear-gradient(145deg,#FDF3E3,#faf0dc,#f5e8cc); border: 1px solid var(--border-color); clip-path: polygon(0 12px,12px 0,calc(100% - 12px) 0,100% 12px,100% calc(100% - 12px),calc(100% - 12px) 100%,12px 100%,0 calc(100% - 12px)); box-shadow: 0 12px 32px rgba(26,4,4,0.14), inset 0 1px 0 rgba(255,255,255,0.7); padding: 0 0 14px; overflow: hidden; }
      .adv-toast-top-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .adv-toast-corner { position: absolute; width: 10px; height: 10px; border-color: rgba(201,151,58,0.45); border-style: solid; }
      .adv-toast-corner.tl { top: 12px; left: 12px; border-width: 1px 0 0 1px; }
      .adv-toast-corner.tr { top: 12px; right: 12px; border-width: 1px 1px 0 0; }
      .adv-toast-corner.bl { bottom: 8px; left: 12px; border-width: 0 0 1px 1px; }
      .adv-toast-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }
      .adv-toast-medallion { position: absolute; left: 18px; top: 26px; width: 36px; height: 36px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .adv-toast-spin-ring { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: slowSpin 8s linear infinite; }
      .adv-toast-icon-inner { font-size: 14px; position: relative; z-index: 1; }
      .adv-toast-content { padding: 0 40px 0 66px; }
      .adv-toast-label-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .adv-toast-symbol { font-family: 'Cinzel Decorative', cursive; font-size: 9px; opacity: 0.7; }
      .adv-toast-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .adv-toast-rule { height: 1px; margin-bottom: 7px; }
      .adv-toast-message { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.75); line-height: 1.5; }
      .adv-toast-bottom-rule { height: 1px; margin: 12px 20px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.25),transparent); }
      .adv-toast-close { position: absolute; top: 22px; right: 14px; background: none; border: none; font-size: 13px; cursor: pointer; opacity: 0.45; transition: opacity 0.2s; }
      .adv-toast-close:hover { opacity: 0.9; }

      .adv-desktop-only { display: table; }
      .adv-mobile-only  { display: none !important; }
      @media (max-width: 768px) {
        .adv-desktop-only { display: none !important; }
        .adv-mobile-only  { display: flex !important; }
      }

      /* ══ MOBILE CARDS ══ */
      .adv-card-list { display: flex; flex-direction: column; gap: 12px; padding-top: 4px; }
      .adv-mob-card { background: linear-gradient(145deg, #FDF3E3, #faf0dc); border: 1px solid rgba(201,151,58,0.2); clip-path: polygon(0 10px,10px 0,calc(100% - 10px) 0,100% 10px,100% calc(100% - 10px),calc(100% - 10px) 100%,10px 100%,0 calc(100% - 10px)); box-shadow: 0 2px 12px rgba(26,4,4,0.08); animation: rowIn 0.35s ease both; overflow: hidden; position: relative; }
      .adv-mob-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.5), transparent); }
      .adv-mob-card-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 14px 10px; border-bottom: 1px solid rgba(201,151,58,0.1); }
      .adv-mob-receipt { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; color: var(--gold-dim); }
      .adv-mob-meta { padding: 10px 14px; display: flex; flex-direction: column; gap: 5px; border-bottom: 1px solid rgba(201,151,58,0.08); }
      .adv-mob-meta-item { display: flex; align-items: flex-start; gap: 7px; font-family: 'EB Garamond', serif; font-size: 13px; color: rgba(74,26,8,0.6); }
      .adv-mob-meta-icon { color: var(--gold-dim); font-size: 11px; margin-top: 2px; flex-shrink: 0; }
      .adv-mob-actions { display: flex; gap: 6px; flex-wrap: wrap; padding: 10px 14px 12px; }
      .adv-mob-btn { display: inline-flex; align-items: center; gap: 5px; font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; border: 1px solid; padding: 7px 12px; cursor: pointer; background: none; transition: all 0.18s; clip-path: polygon(6px 0%,calc(100% - 6px) 0%,100% 50%,calc(100% - 6px) 100%,6px 100%,0 50%); }
      .adv-mob-btn.mob-view    { border-color: rgba(201,151,58,0.35); color: var(--gold-dim); }
      .adv-mob-btn.mob-view:hover { background: rgba(201,151,58,0.1); }
      .adv-mob-btn.mob-delete  { border-color: rgba(139,26,26,0.22); color: rgba(139,26,26,0.7); margin-left: auto; }
      .adv-mob-btn.mob-delete:hover { background: rgba(139,26,26,0.08); color: #8b1a1a; }

      @media (max-width: 768px) {
        .adv-hero { padding: 22px 16px 0; border-radius: 0; }
        .adv-hero-medallion { width: 68px; height: 68px; margin-bottom: 12px; }
        .adv-hero-om { font-size: 24px; }
        .adv-hero-logo-img { width: 40px; height: 40px; }
        .adv-hero-title { font-size: 19px; }
        .adv-hero-sub { font-size: 13px; margin-bottom: 12px; }
        .adv-stat-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; width: 100%; padding: 0 0 18px; }
        .adv-stat-card { min-width: unset; padding: 12px 10px; clip-path: none; border-radius: 8px; }
        .adv-toolbar { padding: 12px 14px 10px; gap: 9px; flex-direction: column; align-items: stretch; }
        .adv-search-wrap { min-width: unset; width: 100%; clip-path: none; border-radius: 28px; }
        .adv-filter-trigger-btn { width: 100%; clip-path: none; border-radius: 28px; justify-content: space-between; padding: 11px 16px; }
        .adv-filter-panel { padding: 12px 14px; }
        .adv-filter-row { flex-direction: column; gap: 12px; }
        .adv-filter-range { min-width: unset; }
        .adv-range-inputs { flex-direction: row; }
        .adv-toolbar-btns { width: 100%; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-left: 0; }
        .adv-action-btn { clip-path: none; border-radius: 28px; justify-content: center; padding: 11px 10px; font-size: 9px; }
        .adv-table-wrap { padding: 0 14px 40px; }
        
        .adv-modal-overlay { padding: 0; align-items: flex-end; }
        .adv-regal-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-height: 93vh; border-bottom: none; width: 100%; max-width: 100% !important; }
        .adv-regal-modal::after { content: ''; position: absolute; top: 8px; left: 50%; transform: translateX(-50%); width: 44px; height: 4px; border-radius: 2px; background: rgba(232,196,106,0.28); pointer-events: none; z-index: 20; display: block; }
        .adv-rm-header { padding: 26px 16px 20px; }
        .adv-detail-scroll-body { padding: 16px 16px 28px; }
        .adv-detail-info-grid { grid-template-columns: 1fr; gap: 8px; }
        .adv-detail-action-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
        .adv-confirm-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-width: 100%; width: 100%; border-bottom: none; }
        .adv-btn { clip-path: none; border-radius: 28px; padding: 12px 18px; font-size: 9.5px; }
        .adv-toast-container { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .adv-toast-card { width: 100%; clip-path: none; border-radius: 12px; }
      }
    `}</style>
  );
}